import {
  TransactionItemFromAPI,
  TransactionItemTemplateDisplay,
  TransactionItemWithTracking,
  VatType,
  VatTypeLabel,
} from '../../../../../_interfaces/transactions.interface';
import { AccountingProviderCompany } from '../../../../../_interfaces/accounting.interface';

/**
 * Generates a unique tracking ID for items
 */
export function generateItemId(): string {
  return `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Converts a single API item to a tracked item format
 *
 * @param item - Item from API with full category object
 * @returns Tracked item ready for template/store
 */
export function convertAPIItemToTrackedItem(item: TransactionItemFromAPI): TransactionItemWithTracking {
  return {
    _trackingId: generateItemId(),
    category: item.category.id,
    name: item.name || '',
    amount: item.amount || null,
    unit_price: item.unit_price,
    unit_quantity: item.unit_quantity,
    tracking: item.tracking?.map((t) => t.id) ?? [], // Convert tracking categories to IDs
    tax: item.tax?.id, // Convert tax rate to ID
    vat: item.vat, // Preserve VAT type
  };
}

/**
 * Converts multiple API items to a Map grouped by category
 * Useful for populating the items store in edit components
 *
 * @param savedItems - Array of items from API
 * @returns Map of categoryId -> array of tracked items
 */
export function convertAPIItemsToStore(
  savedItems: TransactionItemFromAPI[],
): Map<number, TransactionItemWithTracking[]> {
  const store = new Map<number, TransactionItemWithTracking[]>();

  savedItems.forEach((item) => {
    const categoryId = item.category.id;
    const trackedItem = convertAPIItemToTrackedItem(item);

    const existingItems = store.get(categoryId) || [];
    store.set(categoryId, [...existingItems, trackedItem]);
  });

  return store;
}

/**
 * Converts items from API format (TransactionItemFromAPI[]) to template-friendly format (TransactionItemTemplateDisplay[])
 * Groups items by category and calculates subtotals
 *
 * @param savedItems - Array of items from API with full category objects
 * @returns Array of template display objects with items grouped by category
 */
export function convertAPIItemsToTemplateDisplay(
  savedItems: TransactionItemFromAPI[],
): TransactionItemTemplateDisplay[] {
  // Group items by category
  const categoryMap = new Map<number, TransactionItemWithTracking[]>();

  savedItems.forEach((item) => {
    const categoryId = item.category.id;

    // Convert API item to tracked item
    const trackedItem: TransactionItemWithTracking = {
      _trackingId: generateItemId(),
      category: categoryId,
      name: item.name || '',
      amount: item.amount || null,
      tracking: item.tracking?.map((t) => t.id) ?? [], // Convert tracking categories to IDs
      tax: item.tax?.id, // Convert tax rate to ID
      vat: item.vat, // Preserve VAT type
    };

    const existingItems = categoryMap.get(categoryId) || [];
    categoryMap.set(categoryId, [...existingItems, trackedItem]);
  });

  // Convert to template display format
  const templateData: TransactionItemTemplateDisplay[] = [];

  categoryMap.forEach((items, categoryId) => {
    // Get category info from the first saved item (all items in a group have the same category)
    const categoryInfo = savedItems.find((si) => si.category.id === categoryId)?.category;

    if (!categoryInfo) {
      return; // Skip if category info not found
    }

    // Calculate subtotal for this category
    const subtotal = items.reduce((sum, item) => {
      const amount = item.amount ? Number.parseFloat(item.amount) : null;
      return amount === null ? sum : sum + amount;
    }, 0);

    templateData.push({
      category: {
        name: categoryInfo.name,
        value: categoryId,
        account_code: categoryInfo.account?.code || '',
      },
      items: items,
      subtotal: subtotal,
    });
  });

  return templateData;
}

// Translate Provider API errors to more readable messages

export function translateSyncAuthError(error: string) {
  // actual responses:
  // Xero --> "Xero api error (transaction_exists) [403:Forbidden]"
  // Quickbooks --> HTTP status 400, error message: b'{"error":"invalid_grant","error_description":"Incorrect or invalid refresh token"}', intuit_tid xxx at time Mon, 18 May 2026 11:03:32 GMT
  return ['forbidden', '403', 'invalid refresh token'].some((str) => error.toLowerCase().includes(str));
}

export function translateProviderError(provider: AccountingProviderCompany, error: string): string | null {
  if (provider === AccountingProviderCompany.XERO) {
    // if any unmapped category selected (normal or split)
    // actual response: Xero api error (upload_transaction) [400:Bad Request]
    if (['bad request'].some((str) => error.toLowerCase().includes(str))) {
      return `The selected category is not synced with your ${provider} account. `;
    }

    // if invalid receipt URL
    // actual response: [Errno 2] No such file or directory: '/app/media_root/receipts/f708bc5a-6b1d-4e4d-ba15-ea02a1da5765/Screenshot_2026-05-11_at_19.23.02_38a939de58ff4b43b870b6b4da2dadc5.png?response-content-type=image%2Fpng&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=test%2F20260518%2Faf-south-1%2Fs3%2Faws4_request&X-Amz-Date=20260518T100053Z&X-Amz-Expires=3600&X-Amz-SignedHeaders=host&X-Amz-Signature=bbe8b563deb85abd60d6f37992b9dbdc71d4db455c10712cb2d5dbf07409df6a'
    if (['no such file or directory'].some((str) => error.toLowerCase().includes(str))) {
      return 'Receipt file appears to be invalid, please re-upload. ';
    }

    // if invalid tax type selected for the relative account
    // actual response: Xero api error (upload_transaction) [400:The TaxType code 'ZERORATED' cannot be used with account code '404'.]
    if (['taxtype'].some((str) => error.toLowerCase().includes(str))) {
      return `The selected tax type "${parseXeroTaxAccountError(error)?.taxType}" is invalid for the account code "${parseXeroTaxAccountError(error)?.accountCode}". `;
    }

    // No matching Xero error pattern
    return null;
  } else if (provider === AccountingProviderCompany.QUICKBOOKS) {
    // if any unmapped category selected (normal or split)
    // actual response: NoneType' object has no attribute 'acid'
    if (['no attribute', 'acid'].some((str) => error?.toLowerCase().includes(str))) {
      return `The selected category is not synced with your ${provider} account.`;
    }

    // No matching QuickBooks error pattern
    return null;
  }

  // Unknown provider or no matching error pattern
  return null;
}

/**
 * Parse Xero API error to extract tax type and account code
 * @param errorMessage - The error message from Xero API
 * @returns Object with taxType and accountCode, or null if parsing fails
 *
 * @example
 * parseXeroTaxAccountError("Xero api error (upload_transaction) [400:The TaxType code 'ZERORATED' cannot be used with account code '404'.]")
 * // Returns: { taxType: 'ZERORATED', accountCode: '404' }
 */
export function parseXeroTaxAccountError(errorMessage: string): { taxType: string; accountCode: string } | null {
  // Regular expression to match: TaxType code 'XXXX' ... account code 'YYY'
  const regex = /TaxType code '([^']+)'.*account code '([^']+)'/;
  const match = new RegExp(regex).exec(errorMessage);

  if (match?.[1] && match[2]) {
    if (match?.[1] === 'ZERORATED') {
      match[1] = 'ZERO RATED';
    }
    return {
      taxType: match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase(),
      accountCode: match[2],
    };
  }

  return null;
}

export function returnVatTypeLabel(value: VatType | undefined | null): VatTypeLabel | undefined {
  if (value === undefined || value === null) return undefined;
  if (value === VatType.EXEMPT) return VatTypeLabel.EXEMPT;
  if (value === VatType.ZERO_RATE) return VatTypeLabel.ZERO_RATE;
  if (value === VatType.STANDARD) return VatTypeLabel.STANDARD;
  if (value === VatType.CAPITALGOODS) return VatTypeLabel.CAPITALGOODS;
  if (value === VatType.NOVAT) return VatTypeLabel.NOVAT;
  return undefined;
}

export function retrieveFileTypeFromS3Url(url: string): string | null {
  if (!url) return null;

  // Remove query parameters by splitting at '?'
  const urlWithoutParams = url.split('?')[0];

  // Get the last 4 characters (file extension)
  const extension = urlWithoutParams.slice(-4).toLowerCase();

  // Map file extensions to file types
  if (extension === '.pdf') {
    return 'pdf';
  }

  // Check for common image formats
  const imageExtensions = [
    '.jpg',
    'jpeg',
    '.jxr',
    '.png',
    '.gif',
    '.bmp',
    'webp',
    '.tif',
    'tiff',
    'heic',
    'heif',
    '.svg',
    'svgz',
  ];
  if (imageExtensions.some((ext) => extension.includes(ext))) {
    return 'image';
  }

  // Return null for unknown file types
  return null;
}

export const imageFormats = ['jpg', 'jpeg', 'jxr', 'png', 'gif', 'bmp', 'webp', 'tif', 'tiff', 'heic', 'heif'];
